"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHvacFilterStatus = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
/**
 * Hook to fetch and manage HVAC filter status data for a specific sensor
 *
 * @param sensorId - The ID of the sensor to get filter status for
 * @returns Object containing the filter status data, loading state, error state, and refresh function
 */
const useHvacFilterStatus = (sensorId) => {
    const [filterStatus, setFilterStatus] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    /**
     * Fetch HVAC filter status for a specific sensor
     */
    const fetchFilterStatus = (0, react_1.useCallback)(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getHVACFilterStatus,
                variables: {
                    sensorId: id,
                },
            });
            const data = response?.data?.getHVACFilterStatus;
            if (data) {
                setFilterStatus(data);
            }
            else {
                setFilterStatus(null);
            }
        }
        catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch HVAC filter status'));
            setFilterStatus(null);
        }
        finally {
            setLoading(false);
        }
        // Removing logger from dependencies as it's stable enough for this use case
        // Ignoring logger as a dependency to prevent unnecessary re-renders
    }, []);
    /**
     * Refresh the HVAC filter status data
     */
    const refresh = (0, react_1.useCallback)(() => {
        if (sensorId) {
            fetchFilterStatus(sensorId);
        }
    }, [sensorId, fetchFilterStatus]);
    // Initial fetch when sensorId is available
    (0, react_1.useEffect)(() => {
        if (sensorId) {
            // Add debounce to prevent excessive API calls
            const timer = setTimeout(() => {
                fetchFilterStatus(sensorId);
            }, 300);
            return () => clearTimeout(timer);
        }
        else {
            setFilterStatus(null);
        }
    }, [sensorId, fetchFilterStatus]);
    return {
        filterStatus,
        loading,
        error,
        refresh,
    };
};
exports.useHvacFilterStatus = useHvacFilterStatus;
